import React, {useMemo} from 'react';
import useI18n from 'i18n/useI18n';
import FormSection from 'containers/Form/FormSection';
import { SwitchButton } from '@etiquette-ui/selectors';
import Switches from 'pages/MenuItems/components/Switches';
import DaysSelector from 'pages/MenuItems/components/DaysSelector';
import { Subtitle, SubtitleSmall } from '@etiquette-ui/typography';
import SecondaryButton from 'pages/MenuItems/components/SecondaryButton';
import TimeRangeSelector from 'pages/MenuItems/components/TimeRangeSelector';

import { ACTIONS } from '../../Reducer';
import CATEGORY_KEYS, {CATEGORY_CONFIG_KEYS} from '../../keys';

import {
  Rule,
  Switch,
  RangeContainer,
  ButtonContainer,
  CustomFormSection,
} from './styles';

const INIT_RULE = {
  days: [],
  isActive: true,
  allDayAvailable: false,
  timeRange: {start: null, end: null},
}

const Availability = ({formData, dispatch}) => {
  const { translate } = useI18n();

  const rules = useMemo(()=>{
    const {schedule} = formData[CATEGORY_KEYS.CONFIG][CATEGORY_CONFIG_KEYS.AVAILABILITY];
  
    if(schedule.size === 0)
      schedule.set(`${new Date()}`,INIT_RULE);
    
    return schedule;
  },[formData[CATEGORY_KEYS.CONFIG][CATEGORY_CONFIG_KEYS.AVAILABILITY]]);

  const disabledDays = useMemo(()=>{
    const items = Array.from(rules.values()).filter(rule => rule.allDayAvailable);
    
    let disabledDays = new Set();
    if(items.length > 0)
      items.forEach( item => disabledDays = disabledDays.union(new Set(item.days)));

    return ({
      exists: items.length > 0,
      days: [...disabledDays]
    });
  },[rules.values()]);

  const updateFormData = (value) => {
    dispatch(ACTIONS.CONFIG_AVAILABILITY, value);
  }

  const onEdit = (idx, key, value) => {
    let newRules = rules;
    let rule = newRules.get(idx);
    
    newRules.set(idx, {
      ...rule,
      [key]: value
    })
    updateFormData({schedule: newRules});
  }

  const onChangeAvailability = () => {
    updateFormData({
      alwaysAvailable: !formData[CATEGORY_KEYS.CONFIG][CATEGORY_CONFIG_KEYS.AVAILABILITY].alwaysAvailable
    });
  }

  const addNewRule = () => {
    const newRules = rules;
    newRules.set(`${new Date()}`, INIT_RULE);
    updateFormData({schedule: newRules});
  }

  const deleteRule = (idx) => {
    const newRules = rules;
    newRules.delete(idx);
    updateFormData({schedule: newRules});
  }

  return (
    <FormSection>
      <Subtitle style={{marginBottom: '15px'}}>{translate('Availability')}</Subtitle>
      <Switch onClick={onChangeAvailability}>
        <SwitchButton active={formData[CATEGORY_KEYS.CONFIG][CATEGORY_CONFIG_KEYS.AVAILABILITY].alwaysAvailable} className={'switch-button'}/>
        <div className="label">
          {translate('Always Available')}
        </div>
      </Switch>
      {!formData[CATEGORY_KEYS.CONFIG][CATEGORY_CONFIG_KEYS.AVAILABILITY].alwaysAvailable && (
        <CustomFormSection>
          <SubtitleSmall style={{marginBottom: '15px'}}>{translate('Schedule')}</SubtitleSmall>
          {Array.from(rules.entries()).map( ([idx, rule]) => (
            <Rule id={idx}>
              <RangeContainer>
                <Switch className='--secondary' onClick={()=>onEdit(idx, 'allDayAvailable', !rule.allDayAvailable)}>
                  <SwitchButton active={rule.allDayAvailable} className={'switch-button'}/>
                  <div className="label">
                    {translate('Available at all times of the day')}
                  </div>
                </Switch>
                <div className={`row ${rule.allDayAvailable ? '--disabled' : ''}`}>
                  <TimeRangeSelector
                    startProps={{
                      value: rule.timeRange.start,
                      onChange: (value)=>onEdit(idx, 'timeRange', {start: value, end: rule.timeRange.end}),
                    }}
                    endProps={{
                      value: rule.timeRange.end,
                      onChange: (value)=>onEdit(idx, 'timeRange', {start: rule.timeRange.start, end: value}),
                    }}
                  />
                </div>         
              </RangeContainer>
              <DaysSelector
                containerClassName='days-selector'
                selectedDays={rule.days} 
                onChange={(days)=>onEdit(idx, 'days', days)}
                disabledDays={disabledDays.days}
                allowDisabledBeMarked={rule.allDayAvailable}
              />
              {rules.size > 1 && (
                <div className='switches'>
                  <Switches
                    active={rule.isActive}
                    onDisable={()=>onEdit(idx, 'isActive', !rule.isActive)}
                    onDelete={()=>deleteRule(idx)}
                    includeDeleteOption={rules.size > 1}
                  />
                </div>
              )}              
            </Rule>
          ))}
          <ButtonContainer>
            <SecondaryButton
              label={translate('Add New Rule')}
              onClick={addNewRule}
            />
          </ButtonContainer>    
        </CustomFormSection>
      )}      
    </FormSection>
  );
}
export default Availability;